import { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { TransactionV2 } from '../../../types/dashboardV2';
import {
  getMobileTransactionStatusBadgeClass,
  getMobileTransactionStatusDotClass,
  formatTransactionStatus,
} from '../../../shared/utils/statusUtils';
import { CopyIdButton } from '../../../components/common/CopyIdButton/CopyIdButton';
import { formatDate, formatTime } from '../../../shared/utils/dateFormat';
import { MobileStat } from '../components/MobileCardParts';

interface MobileTransactionCardProps {
  transaction: TransactionV2;
  onViewDetails: (transaction: TransactionV2) => void;
}

// rerender-memo: Memoize list item component to prevent unnecessary re-renders
export const MobileTransactionCard = memo(function MobileTransactionCard({
  transaction,
  onViewDetails,
}: MobileTransactionCardProps) {
  const { t } = useTranslation();

  const handleDetailsClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    onViewDetails(transaction);
  }, [onViewDetails, transaction]);

  return (
    <div
      className="mobile-station-card"
      role="article"
      aria-label={t('dashboard.mobileCards.transactionAria', { id: transaction.tranId || '—' })}
    >
      {/* Header with Transaction ID and Status */}
      <div className="mobile-card-header">
        <div>
          <span className="mobile-card-id-label">{t('dashboard.mobileCards.transaction')}</span>
          <h3 className="mobile-card-id-value">{transaction.tranId || '-'}</h3>
        </div>
        <span className={`mobile-status-badge ${getMobileTransactionStatusBadgeClass(transaction.status)}`}>
          <span className={`mobile-status-dot ${getMobileTransactionStatusDotClass(transaction.status)}`} />
          {formatTransactionStatus(transaction.status)}
        </span>
      </div>

      {/* Stats Grid - 2x2 */}
      <div className="mobile-card-stats mobile-card-stats-2x2">
        <MobileStat
          icon="payments"
          label={t('dashboard.mobileCards.amount')}
          value={`${transaction.amount.toFixed(2)} ₴`}
        />
        <MobileStat
          icon="account_balance"
          label={t('dashboard.mobileCards.bank')}
          value={transaction.bank || '-'}
        />
        <MobileStat
          icon="calendar_today"
          label={t('dashboard.mobileCards.date')}
          value={formatDate(transaction.createdAt)}
        />
        <MobileStat
          icon="schedule"
          label={t('dashboard.mobileCards.time')}
          value={formatTime(transaction.createdAt)}
        />
      </div>

      {/* User row with copy */}
      {transaction.userId && (
        <div className="mobile-card-user-row">
          <span className="material-symbols-outlined" aria-hidden="true">person</span>
          <span className="mobile-card-user-id" title={transaction.userId}>
            {transaction.userId}
          </span>
          <CopyIdButton value={transaction.userId} label={t('dashboard.tables.sessions.copyUserId')} variant="icon" />
        </div>
      )}

      {/* Footer with Details button */}
      <div className="mobile-card-footer">
        <div className="mobile-card-address">
          <span className="material-symbols-outlined">receipt_long</span>
          <span className="mobile-card-address-text">
            {transaction.userId ? formatTime(transaction.createdAt) : t('dashboard.mobileCards.unknownUser')}
          </span>
        </div>
        <button
          type="button"
          className="mobile-details-btn"
          onClick={handleDetailsClick}
          disabled={!transaction.invoiceId}
          title={transaction.invoiceId ? t('dashboard.mobileCards.viewTransactionDetails') : t('dashboard.mobileCards.noInvoiceTitle')}
          aria-label={transaction.invoiceId ? t('dashboard.mobileCards.viewTransactionDetails') : t('dashboard.mobileCards.noInvoice')}
        >
          <span className="material-symbols-outlined" aria-hidden="true">visibility</span>
        </button>
      </div>
    </div>
  );
});

export default MobileTransactionCard;
